import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { lazy, Suspense } from "react";
const InteractiveCursor = lazy(() => import("@/components/3d/InteractiveCursor"));
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Index from "./pages/Index";

const Shop = lazy(() => import("./pages/Shop"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Account = lazy(() => import("./pages/Account"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const ShippingReturns = lazy(() => import("./pages/ShippingReturns"));
const FAQ = lazy(() => import("./pages/FAQ"));
const SizeGuide = lazy(() => import("./pages/SizeGuide"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Sell = lazy(() => import("./pages/Sell"));
const SellerDashboard = lazy(() => import("./pages/SellerDashboard"));
const SellerPlans = lazy(() => import("./pages/SellerPlans"));
const SellerLogin = lazy(() => import("./pages/SellerLogin"));
const Affiliate = lazy(() => import("./pages/Affiliate"));
const AffiliateDashboard = lazy(() => import("./pages/AffiliateDashboard"));
const RefRedirect = lazy(() => import("./pages/RefRedirect"));
const ESignSign = lazy(() => import("./pages/ESignSign"));
const SellerStorefront = lazy(() => import("./pages/SellerStorefront"));

const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminProducts = lazy(() => import("./pages/admin/Products"));
const AdminProductEditor = lazy(() => import("./pages/admin/ProductEditor"));
const AdminOrders = lazy(() => import("./pages/admin/Orders"));
const AdminOrderDetail = lazy(() => import("./pages/admin/OrderDetail"));
const AdminCustomers = lazy(() => import("./pages/admin/Customers"));
const AdminCustomerDetail = lazy(() => import("./pages/admin/CustomerDetail"));
const AdminCoupons = lazy(() => import("./pages/admin/Coupons"));
const AdminDraftOrders = lazy(() => import("./pages/admin/DraftOrders"));
const AdminGiftCards = lazy(() => import("./pages/admin/GiftCards"));
const AdminAbandonedCheckouts = lazy(() => import("./pages/admin/AbandonedCheckouts"));
const AdminPlaceholder = lazy(() => import("./components/admin/AdminPlaceholder"));
const AdminPayments = lazy(() => import("./pages/admin/Payments"));
const AdminCategories = lazy(() => import("./pages/admin/Categories"));
const AdminReviews = lazy(() => import("./pages/admin/Reviews"));
const AdminMarketing = lazy(() => import("./pages/admin/Marketing"));
const AdminRoles = lazy(() => import("./pages/admin/Roles"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminShipping = lazy(() => import("./pages/admin/Shipping"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics"));
const AdminEmails = lazy(() => import("./pages/admin/Emails"));
const AdminInventory = lazy(() => import("./pages/admin/Inventory"));
const AdminReturns = lazy(() => import("./pages/admin/Returns"));
const AdminActivityLog = lazy(() => import("./pages/admin/ActivityLog"));
const AdminMessages = lazy(() => import("./pages/admin/Messages"));
const AdminStaffPermissions = lazy(() => import("./pages/admin/StaffPermissions"));
const AdminESign = lazy(() => import("./pages/admin/ESign"));
const AdminPayroll = lazy(() => import("./pages/admin/Payroll"));
const AdminInvoices = lazy(() => import("./pages/admin/Invoices"));
const AdminSellerApplications = lazy(() => import("./pages/admin/SellerApplications"));
const AdminSellers = lazy(() => import("./pages/admin/Sellers"));
const AdminAffiliates = lazy(() => import("./pages/admin/Affiliates"));
const AdminAffiliateAnalytics = lazy(() => import("./pages/admin/AffiliateAnalytics"));
const AdminSellerPlans = lazy(() => import("./pages/admin/SellerPlansAdmin"));

const AdminStaffList = lazy(() => import("./pages/admin/staff/StaffList"));
const AdminStaffAttendance = lazy(() => import("./pages/admin/staff/StaffAttendance"));
const AdminStaffLeaves = lazy(() => import("./pages/admin/staff/StaffLeaves"));
const AdminStaffPayouts = lazy(() => import("./pages/admin/staff/StaffPayouts"));
const AdminStaffDocuments = lazy(() => import("./pages/admin/staff/StaffDocuments"));
const AdminAnnouncements = lazy(() => import("./pages/admin/staff/Announcements"));
const AdminCompanyCalendar = lazy(() => import("./pages/admin/staff/CompanyCalendar"));

const StaffLayout = lazy(() => import("./components/staff/StaffLayout"));
const StaffDashboard = lazy(() => import("./pages/staff/Dashboard"));
const StaffAttendancePage = lazy(() => import("./pages/staff/Attendance"));
const StaffCalendarPage = lazy(() => import("./pages/staff/Calendar"));
const StaffLeavesPage = lazy(() => import("./pages/staff/Leaves"));
const StaffPayoutsPage = lazy(() => import("./pages/staff/Payouts"));
const StaffDocumentsPage = lazy(() => import("./pages/staff/Documents"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function AppRoutes() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isStaff = location.pathname === "/staff" || location.pathname.startsWith("/staff/");
  const isSellerDash = location.pathname.startsWith("/sell/dashboard") || location.pathname.startsWith("/sell/plans");
  const isSellerLogin = location.pathname === "/sell/login";
  const isESignPublic = location.pathname.startsWith("/esign/sign/");
  const hideChrome = isAdmin || isStaff || isSellerDash || isSellerLogin || isESignPublic;

  return (
    <>
      {!hideChrome && <Navbar />}
      <main className={hideChrome ? "" : "min-h-screen"}>
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/account" element={<Account />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/shipping-returns" element={<ShippingReturns />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/size-guide" element={<SizeGuide />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/sell" element={<Sell />} />
            <Route path="/sell/dashboard" element={<SellerDashboard />} />
            <Route path="/sell/plans" element={<SellerPlans />} />
            <Route path="/sell/login" element={<SellerLogin />} />
            <Route path="/affiliate" element={<Affiliate />} />
            <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
            <Route path="/ref/:code" element={<RefRedirect />} />
            <Route path="/esign/sign/:token" element={<ESignSign />} />
            <Route path="/store/:slug" element={<SellerStorefront />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="products/new" element={<AdminProductEditor />} />
              <Route path="products/:id" element={<AdminProductEditor />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="orders/:id" element={<AdminOrderDetail />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="customers/:id" element={<AdminCustomerDetail />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="draft-orders" element={<AdminDraftOrders />} />
              <Route path="gift-cards" element={<AdminGiftCards />} />
              <Route path="abandoned-checkouts" element={<AdminAbandonedCheckouts />} />
              <Route path="marketing" element={<AdminMarketing />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="shipping" element={<AdminShipping />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="emails" element={<AdminEmails />} />
              <Route path="inventory" element={<AdminInventory />} />
              <Route path="returns" element={<AdminReturns />} />
              <Route path="activity" element={<AdminActivityLog />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="permissions" element={<AdminStaffPermissions />} />
              <Route path="esign" element={<AdminESign />} />
              <Route path="payroll" element={<AdminPayroll />} />
              <Route path="sellers" element={<AdminSellers />} />
              <Route path="seller-applications" element={<AdminSellerApplications />} />
              <Route path="seller-plans" element={<AdminSellerPlans />} />
              <Route path="affiliates" element={<AdminAffiliates />} />
              <Route path="affiliate-analytics" element={<AdminAffiliateAnalytics />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route path="roles" element={<AdminRoles />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="staff" element={<AdminStaffList />} />
              <Route path="staff/attendance" element={<AdminStaffAttendance />} />
              <Route path="staff/leaves" element={<AdminStaffLeaves />} />
              <Route path="staff/payouts" element={<AdminStaffPayouts />} />
              <Route path="staff/documents" element={<AdminStaffDocuments />} />
              <Route path="staff/announcements" element={<AdminAnnouncements />} />
              <Route path="staff/calendar" element={<AdminCompanyCalendar />} />
            </Route>

            <Route path="/staff" element={<StaffLayout />}>
              <Route index element={<StaffDashboard />} />
              <Route path="attendance" element={<StaffAttendancePage />} />
              <Route path="calendar" element={<StaffCalendarPage />} />
              <Route path="leaves" element={<StaffLeavesPage />} />
              <Route path="payouts" element={<StaffPayoutsPage />} />
              <Route path="documents" element={<StaffDocumentsPage />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {!hideChrome && <Footer />}
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <ThemeProvider>
        <AuthProvider>
          <WishlistProvider>
            <CartProvider>
              <Suspense fallback={null}><InteractiveCursor /></Suspense>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
            </CartProvider>
          </WishlistProvider>
        </AuthProvider>
      </ThemeProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
